import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IPaymentTransactionModel } from '../../../../entities-generated/models/project/payment-transaction-model';
import { noWrapStyle } from '../../../../framework/utils/no-wrap-style';
import { PaymentTransactionType } from '../../../../entities-generated/common/payment-transaction-type';
import { BwjPalette } from '../../../../app-core/app-theme';
import { toDate } from '../../../../framework/utils/date-time';
import { formatString } from '../../../../framework/utils/string';
import { PriceDisplay } from '../../../shop/components/price';
import { BwjCard } from '../../../../framework/components/bwj-layout/card';

interface ITransactionListingProps {
  transaction: IPaymentTransactionModel;
  balance: number;
}

const getStyleClasses = (theme: ITheme, tagColor: string) => {
  const root: IStyle = {
    borderTopLeftRadius: 0,
    width: '100%',
    boxSizing: 'border-box',
    padding: 0,
  };

  const content: IStyle = {
    flexGrow: 1,
    width: 1,
  };

  const topLine: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };
  const label: IStyle = {
    ...noWrapStyle as any,
    display: 'block',
    position: 'relative',
    margin: 0,
    paddingTop: '0.3rem',
    paddingBottom: 0,
    paddingLeft: '0.6rem',
    paddingRight: '0.6rem',
    height: 28,
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    color: theme.palette.white,
    fontWeight: 600,
    backgroundColor: tagColor,
    overflow: 'visible',
    boxSizing: 'border-box',

    '& > i': {
      display: 'block',
      width: 0,
      height: 0,
      position: 'absolute',
      right: -28,
      bottom: -28,
      border: '28px solid transparent',
      borderTopColor: tagColor,
    borderLeftWidth: 0,
    },
  };

  const pricing: IStyle = {
    padding: '0.3rem',
    paddingRight: '0.75rem',
    display: 'flex',
    flexDirection: 'row',
  };

  const priceInfo: IStyle = {
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    color: theme.palette.neutralPrimary,
    fontWeight: 600,
  };

  const details: IStyle = {
    padding: '0 0.75rem',
  };

  const heading: IStyle = {
    ...noWrapStyle as any,
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    margin: 0,
    marginTop: '0.5rem',
    marginBottom: '0.75rem',
    color: theme.palette.neutralPrimary,
    fontWeight: 600,
  };

  const bottomLine: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '0.75rem',

    '& span': {
      ...noWrapStyle as any,
      fontSize: theme.fonts.small.fontSize,
      fontWeight: 400,
      lineHeight: theme.fonts.small.lineHeight,
    },
  };

  return mergeStyleSets({
    root,
    content,
    topLine,
    label,
    pricing,
    priceInfo,
    details,
    heading,
    bottomLine,
  });
}

const getTagColor = (t: PaymentTransactionType) => {
  if(t === PaymentTransactionType.CustomerPayment
    || t === PaymentTransactionType.Chargeback
  ) {
    return BwjPalette.themeError;
  }
  if(t === PaymentTransactionType.ChargebackAcknowledgement
    || t === PaymentTransactionType.RefundIssuance
  ) {
    return BwjPalette.themeSuccess;
  }

  return BwjPalette.accentPrimary;
}

const toBalanceString = (balance: number) => {
  const negative = balance < 0;
  const b = balance / 100;
  return negative ? `-$${b.toFixed(2)}` : `$${b.toFixed(2)}`;
}

const toTransactionType = (t: PaymentTransactionType) => {
  if(t === PaymentTransactionType.Invoice) {
    return 'Invoice';
  }
  if(t === PaymentTransactionType.RefundInvoice) {
    return 'Credit due';
  }
  if(t === PaymentTransactionType.CustomerPayment) {
    return 'Customer payment';
  }
  if(t === PaymentTransactionType.Chargeback) {
    return 'Chargeback';
  }
  if(t === PaymentTransactionType.RefundIssuance) {
    return 'Refund payment';
  }
  if(t === PaymentTransactionType.ChargebackAcknowledgement) {
    return 'Approved chargeback';
  }

  return '';
}

export const TransactionListing: React.FunctionComponent<ITransactionListingProps> = (props) => {
  const { transaction } = props;
  const theme = getTheme();
  const cs = useMemo(() => {
    const tagColor = getTagColor(transaction.transactionType);
    return getStyleClasses(theme, tagColor);
  }, [transaction, theme]);
  const date = useMemo(() => {
    const d = toDate(transaction.date);
    return formatString('{d:d-MMMdyyyy}', {d}).formattedString;
  }, [transaction]);

  return (
    <BwjCard
      className={cs.root}
    >
      <div className={cs.topLine}>
        <label
          className={cs.label}
        >
          <i/>
          {date}
        </label>
        <div className={cs.pricing}>
          <PriceDisplay
            price={transaction.amount}
            fontSize={theme.fonts.mediumPlus}
          />
        </div>
      </div>
      <div className={cs.details}>
        <h3
          className={cs.heading}
        >
          {transaction.description}
        </h3>
        <div className={cs.bottomLine}>
          <span>{toTransactionType(transaction.transactionType)}</span>
          <span>Bal: {toBalanceString(props.balance)}</span>
        </div>
      </div>
    </BwjCard>
  );
};
